import { CheckCircleSolid, Clipboard } from '@assets/svgs';
import { Button } from '@components/button';
import { GenerateQuoteCustomerAccessLinkButton } from '@components/button/GenerateQuoteCustomerAccessLinkButton';
import { Dialog } from '@components/dialog';
import { IconButton } from '@components/iconButton';
import { TextField } from '@components/textField/TextField';
import { ToastType } from '@components/toast/ToastType.enum';
import { DEBOUNCE_DELAYS } from '@constants/debounceDelays';
import { engineRegExp } from '@lio/rule-engine';
import { TQuoteApiResponse } from '@models/quotes';
import { Box, Typography } from '@mui/material';
import { useCreateQuoteCustomerInvite } from '@services/quotes/quote-invite/useCreateQuoteCustomerInvite';
import { useCreateQuoteCustomerInviteByEmail } from '@services/quotes/quote-invite/useCreateQuoteCustomerInviteByEmail';
import { useGetScreenSize } from '@utils/useGetScreenSize';
import { enqueueSnackbar } from 'notistack';
import { useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';

type GrantCustomerAccessDialogDialogProps = {
  quote: TQuoteApiResponse;
  isOpen: boolean;
  onClose: () => void;
};

export const GrantCustomerAccessDialog = ({ quote, isOpen, onClose }: GrantCustomerAccessDialogDialogProps) => {
  const { isSmallScreen } = useGetScreenSize();
  const [email, setEmail] = useState('');
  const [hasInvalidEmail, setHasInvalidEmail] = useState(false);
  const [emailErrorMessage, setEmailErrorMessage] = useState<string | null>(null);
  const [disableGenerateLink, setDisableGenerateLink] = useState(false);

  const { createQuoteCustomerInviteByEmail, isPending: isCreatingInviteByEmail } =
    useCreateQuoteCustomerInviteByEmail();
  const { isPending: isCreatingInvite } = useCreateQuoteCustomerInvite();

  const [generatedLinks, setGeneratedLinks] = useState<string[]>([]);
  const [generatedEmailAccess, setGeneratedEmailAccess] = useState<string[]>([]);

  const handleGenerateEmailAccess = async () => {
    setDisableGenerateLink(true);

    try {
      await createQuoteCustomerInviteByEmail({
        quoteId: quote.id,
        email,
      });

      setGeneratedEmailAccess((prev) => [...prev, email]);
      setEmail('');

      enqueueSnackbar(`Customer Access Generated`, {
        preventDuplicate: true,
        variant: ToastType.Success,
      });
    } catch (e: any) {
      if (e?.message === 'User not found') {
        setEmailErrorMessage('User not found');
      }
    }

    // in 1 second, re-enable the button
    setTimeout(() => {
      setDisableGenerateLink(false);
    }, 1000);
  };

  const handleDebouncedEmailChange = useDebouncedCallback(
    (value: string) => {
      if (value === '') {
        setHasInvalidEmail(false);
        return;
      }

      const isValid = engineRegExp.email.regExp.test(value);
      setHasInvalidEmail(!isValid);
    },
    hasInvalidEmail ? 0 : DEBOUNCE_DELAYS.TYPING,
  );

  const isLoading = isCreatingInviteByEmail || isCreatingInvite;
  const emailHasError = hasInvalidEmail || !!emailErrorMessage;

  return (
    <Dialog
      open={isOpen}
      fullScreen={isSmallScreen}
      handleCloseDialog={onClose}
      secondaryBtnAction={onClose}
      secondaryBtnText='Exit'
      isSecondaryBtnDisabled={isLoading}
      showBorders={false}
      title={`Grant Customer Access to Quote #${quote?.policyNumber?.policyNumber ?? ''}`}
      size='small'
    >
      <Box
        sx={{
          margin: '10px 28px 20px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'left',
          gap: 1,
        }}
      >
        <Typography variant='bodySmall' mb={1} sx={{ display: 'block' }}>
          If the Customer did not create this quote, they will not have access to view it. If you would like to grant a
          Customer access to this quote, use one of the methods below:
        </Typography>
        <Typography variant='bodySmall' mb={1} sx={{ display: 'block' }}>
          If the User exists in our system already, please enter their email:
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'left', height: emailHasError ? '80px' : '70px', gap: 0 }}>
          <Box sx={{ flex: 1 }}>
            <TextField
              inputLabel='User Email'
              value={email}
              fullWidth
              onChangeInput={(value) => {
                const v = value.trim();
                setEmailErrorMessage(null);
                setEmail(v);
                handleDebouncedEmailChange(v);
              }}
              helperText={emailErrorMessage ? emailErrorMessage : hasInvalidEmail ? 'Must be valid email address' : ''}
              error={emailHasError}
            />
          </Box>
          <Box sx={{ alignContent: 'center', display: 'flex', alignItems: 'center', mt: emailHasError ? 0 : 1 }}>
            <Button
              disabled={disableGenerateLink || !email || emailHasError || isLoading}
              isLoading={isCreatingInviteByEmail}
              variant='text'
              onClick={handleGenerateEmailAccess}
            >
              Grant Customer Access
            </Button>
          </Box>
        </Box>

        {generatedEmailAccess.length > 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 1 }}>
            {generatedEmailAccess.map((email, index) => (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  svg: {
                    color: 'success.dark',
                    fill: 'success.dark',
                  },
                }}
              >
                <Typography variant='bodySmall' color='text.subdued' sx={{ wordBreak: 'break-all' }}>
                  {email}
                </Typography>

                <CheckCircleSolid height={14} width={14} />
              </Box>
            ))}
          </Box>
        )}

        <Typography variant='bodySmall' sx={{ display: 'block' }}>
          If the User does not exist in our system, or if you would like to reach out to them directly, please copy the
          link below:
        </Typography>
        <GenerateQuoteCustomerAccessLinkButton
          quoteId={quote.id}
          onClickCallback={(inviteLink) => {
            setGeneratedLinks((prev) => [...prev, inviteLink]);
          }}
          sx={{ mt: 1 }}
        />

        {generatedLinks.length > 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, ml: -1 }}>
            {generatedLinks.map((link, index) => (
              <Box key={index} sx={{ display: 'flex', alignItems: 'flex-start' }}>
                <IconButton
                  onClick={() => {
                    navigator.clipboard.writeText(link);
                  }}
                >
                  <Clipboard />
                </IconButton>
                <Typography variant='bodySmall' color='text.subdued' sx={{ wordBreak: 'break-all' }}>
                  {link}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
        <Typography variant='caption' mt={2} sx={{ display: 'block' }}>
          Each generated link can only be used once and will expire after 72 hours.
        </Typography>
      </Box>
    </Dialog>
  );
};
